import React, { useContext, useState } from 'react';
import useComponentConfiguration from 'DisplayFramework/useComponentConfiguration';
import { TitleProps as AntTitleProps } from 'antd/lib/typography/Title';
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Switch,
  Typography
} from 'antd';
import { useDisplayFrameworkContext } from 'DisplayFramework/hooks/useDisplayFrameworkContext';
import { AuthContext } from './Auth/Auth';
import {
  ParPagComponentRequest,
  updatePageComponent
} from 'services/displayFramework';
import { SaveOutlined } from '@ant-design/icons';
import ParListSelect from './ParListSelect';
import { ViewEntityAddAttributeResponse } from 'services/assetconfig';
import TextArea from 'antd/lib/input/TextArea';
import moment from 'moment';

const { Title: AntTitle } = Typography;

export type TitleCardProps = Omit<AntTitleProps, 'children'> & {
  comcode: string;
  children?: React.ReactNode | React.ReactNode[];
  lg?: number;
  md?: number;
  xs?: number;
  xl?: number;
  sm?: number;
  hidden?: boolean;
  noStyle?: boolean;
  noBorder?: boolean;
  styleCard?: React.CSSProperties;
  Inspectstyle?: React.CSSProperties;
  additionalAttribute?: boolean;
  noMarginTop?: boolean;
};

const UtinaCard = ({
  comcode,
  lg,
  md,
  xl,
  xs,
  sm,
  hidden,
  noStyle,
  noBorder,
  styleCard,
  Inspectstyle,
  additionalAttribute,
  noMarginTop,
  ...otherProps
}: TitleCardProps) => {
  const { isHidden, label, style } = useComponentConfiguration(comcode);
  const { pagcode, refresh, getAttributeListByComcode } =
    useDisplayFrameworkContext();
  const { isInspect } = useContext(AuthContext);
  const [newLabel, setNewLabel] = useState('');

  const listAttribute: any = getAttributeListByComcode(comcode);

  if (isHidden) {
    return null;
  }
  const handleSaveNewLabel = (comcode: string, labelp: string) => {
    const request: ParPagComponentRequest = {
      comcode: comcode ?? '',
      comformat: '',
      comhint: '',
      comlabel: newLabel === '' ? label : labelp,
      commessage: '',
      complaceholder: '',
      comrequired: false,
      comstyle: '',
      comtype: '',
      pagcode: pagcode ?? ''
    };
    updatePageComponent({ request: request }).then((res) => {
      if (res.code === '200')
        notification.success({
          message: res.message,
          description: res.description
        });
      else {
        notification.error({
          message: res.message,
          description: res.description
        });
      }
      refresh();
    });
  };

  const props = {
    ...otherProps,
    children: isInspect ? (
      <Input
        defaultValue={label === '' ? comcode : label}
        size="middle"
        onChange={(event) => {
          event.preventDefault();
          setNewLabel(event.target.value);
        }}
        onClick={(event) => {
          event.preventDefault();
        }}
        style={Inspectstyle ?? { width: '80%' }}
        className="input-inspect"
        addonAfter={
          <SaveOutlined
            onClick={(ev) => {
              ev.preventDefault();
              handleSaveNewLabel(comcode, newLabel);
            }}
          />
        }
      />
    ) : (
      label
    ),
    style
  };

  return (
    <Col
      typeof="card"
      className="utinaCard"
      lg={lg ?? 24}
      md={md}
      xl={xl}
      xs={xs ?? 24}
      sm={sm}
      style={{
        borderRadius: '10px',
        border: noBorder ? 0 : '1px',
        borderStyle: noBorder ? 'none' : 'solid',
        borderColor: 'rgb(192 234 230)',
        padding: noStyle ? 0 : 16,
        marginTop: noStyle ? 0 : 20,
        marginLeft: noStyle ? 0 : 10,
        marginRight: noStyle ? 0 : 10,
        ...styleCard
      }}
      hidden={hidden ? hidden : undefined}
    >
      {!noStyle && (
        <AntTitle
          {...props}
          style={{
            backgroundColor: "#fff",
            position: 'absolute',
            top: '-0.8rem',
            padding: '0px 3px 0px 3px',
            color: 'rgb(95 90 90 / 85%)'
          }}
        />
      )}
      {!noMarginTop && <Row gutter={18}>&nbsp;</Row>}

      {additionalAttribute ? (
        <Row gutter={8}>
          {listAttribute?.map(
            (att: ViewEntityAddAttributeResponse, index: number) => (
              /**
               * => Optimisation
               * Map attribute from attribute list and return Col HTML component switch position 'BEGIN/END' And Positionorder
               **/
              <Form.List name="entityAddAttributList" key={att.displayposition}>
                {(data, { add, remove }) => {
                  const {
                    attcode,
                    displaytype,
                    attinputtype,
                    colspan,
                    attlabel,
                    defautvalue,
                    attnbrows,
                    parcode,
                    displayposition,
                    displayorder
                  } = att;
                  const isMandatory = displaytype === 'MANDATORY';

                  const commonFormProps = {
                    rules: [{ required: isMandatory }],
                    name: attcode,
                    key: index,
                    label: (
                      <span style={{ fontWeight: 500 }}>
                        {attlabel ?? attcode}
                      </span>
                    ),
                    initialValue: defautvalue
                  };

                  const spanValue = colspan == null ? 4 : Math.max(4, colspan);

                  const renderFormField = (
                    fieldComponent: any,
                    att: ViewEntityAddAttributeResponse
                     ) => {
                    const isSwitch = att.attinputtype === 'SWITCH'; // Check if the field is a boolean (SWITCH)
                  
                    return (
                      <Col
                        style={{
                          order:
                            att.displayposition === 'BEGIN'
                              ? att.displayorder * -1 + 1
                              : att.displayorder,
                        }}
                        span={spanValue}
                        key={index}
                      >
                        <Form.Item
                          {...commonFormProps}
                          valuePropName={isSwitch ? 'checked' : undefined} // Apply `checked` only for SWITCH
                        >
                          {fieldComponent}
                        </Form.Item>
                      </Col>
                    );
                  };

                  const formFieldMap: any = {
                    LIST: (
                      <ParListSelect
                        parcode={parcode}
                        defaultValue={defautvalue}
                      />
                    ),
                    NUMBER: (
                      <InputNumber
                        defaultValue={defautvalue}
                        step={attinputtype === 'DOUBLE' ? '0.01' : 1}
                      />
                    ),
                    TEXTAREA: (
                      <TextArea
                        rows={attnbrows ?? 4}
                        defaultValue={defautvalue}
                      />
                    ),
                    DATE: <DatePicker defaultValue={moment(defautvalue)} />,
                    SWITCH: (
                      <Switch checkedChildren="YES" unCheckedChildren="NO" defaultChecked={Boolean(defautvalue)} />
                    ),
                    TEXT: (
                      <Input
                        defaultValue={String(defautvalue)}
                        placeholder={defautvalue}
                      />
                    )
                  };

                  const fieldComponent =
                    formFieldMap[attinputtype] || formFieldMap.TEXT;

                  return renderFormField(fieldComponent, att);
                }}
              </Form.List>
            )
          )}

          {otherProps.children}
        </Row>
      ) : (
        otherProps.children
      )}
    </Col>
  );
};
export default UtinaCard;
